import React, { useState, useRef, useEffect } from 'react'
import { startCase, toLower } from 'lodash'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Stack, Button, InputAdornment, Typography, CircularProgress, Badge } from '@mui/material_modern'
import { LinkedCamera } from '@mui/icons-material'

import { useGetOrgImageQuery } from 'services/assets'

import Input from 'components/generic/form/Input'
import ScalePopover from 'components/generic/popovers/ScalePopover'

import { useScaleState } from 'utils/custom-hooks/useScaleState'
import { onKeyPressNumberField } from 'components/containers/inbound-tickets/helpers/materialEntryFormHelpers'
import { isManualValidationInfo } from 'components/containers/inbound-tickets/helpers/grid-validations'
import { formatAxleWeightsFromFormData } from 'components/containers/inbound-tickets/helpers/axleWeightsHelpers'
import { generateRequestImagesFunction } from 'utils/images'
import { GS_ROLE } from 'utils/constants'

const AxleWeightsRow = ({
  type,
  axleNumber,
  numberOfAxles,
  inputFieldProps,
  setAxleRefs,
  setMultiImageBladeData,
  disabled,
  materialData,
}) => {
  const { control, getValues, setValue, watch } = useFormContext()
  const { t } = useTranslation('inboundTicket')

  const pathPrepend = `axles.axle-${axleNumber}`

  const images = watch(`${pathPrepend}.${type}Images`) || []
  const filename = images?.[0]?.imageFilename

  const { data: imageData, isFetching: isFetchingImageData } = useGetOrgImageQuery({ filename }, { skip: !filename })

  const { canFlagManualScaleReadOverrideFF } = useSelector(state => state.featureFlags?.variables || {})
  const { selectedScaleIds, selectedCameraIds, scaleCameras } = useSelector(state => state.devices)
  const isNativeCamera =
    selectedCameraIds?.length === 0 && Object.values(scaleCameras || {}).every(cameras => !cameras?.length)
  const { defaultYardUm, settings } = useSelector(state => state.settings)
  const { showWeightDecimals, flagManuallyEnteredWeights } = settings?.purchaseSettings?.inboundTickets || {}
  const canFlagManualScaleReadOverride = canFlagManualScaleReadOverrideFF && flagManuallyEnteredWeights

  const { roles } = useSelector(state => state.permissions)
  const hasEditPermission = !!roles?.find(
    r =>
      r.roleName === GS_ROLE.YARD_ADMIN || r.roleName === GS_ROLE.SCALE_OPERATOR || r.roleName === GS_ROLE.YARD_MANAGER,
  )

  const fieldRef = useRef(null)
  const [scalePopoverData, setScalePopoverData] = useState({})

  const { isFetchingScaleReading, showScaleButton, readScale } = useScaleState()
  const { requestImages } = generateRequestImagesFunction()

  const callRequestImages = (images, scaleId) => {
    if ((images || []).length === 0 && !isNativeCamera) {
      requestImages(imageData => {
        setValue(`${pathPrepend}.${type}Images`, imageData, { shouldDirty: true })
      }, scaleId)
    }
  }

  const callReadScale = ({ scaleId = '' }) => {
    readScale(scaleData => {
      setValue(`${pathPrepend}.${type}`, scaleData.weight, { shouldDirty: true })
      fieldRef?.current?.focus()
      if (canFlagManualScaleReadOverride) {
        setValue(`${pathPrepend}.isManual${startCase(toLower(type))}`, false)
      }
    }, scaleId)
  }

  const isManual = watch(`${pathPrepend}.isManual${startCase(toLower(type))}`) || false
  const { color, startAdornment } = isManualValidationInfo(isManual, canFlagManualScaleReadOverride)

  const scaleButtonClickHandler = event => {
    if (selectedScaleIds?.length > 1) {
      setScalePopoverData({
        anchorEl: event.target,
      })
    } else {
      callReadScale({})
      callRequestImages(images, selectedScaleIds[0])
    }
  }

  useEffect(() => {
    setAxleRefs(prevAxleRefs => ({
      ...prevAxleRefs,
      [`${type}${axleNumber}`]: fieldRef,
    }))
  }, [fieldRef])

  const renderImagePreview = () => {
    if (!filename) {
      return (
        <Button variant="outlined" sx={{ width: '75px', borderColor: 'borderColor.default' }}>
          <LinkedCamera sx={{ color: 'text.disabled' }} />
        </Button>
      )
    }
    if (filename && isFetchingImageData) {
      return (
        <Button variant="outlined" sx={{ width: '75px', borderColor: 'borderColor.default' }}>
          <Typography align="center">
            <CircularProgress size="1rem" sx={{ color: 'text.disabled' }} />
          </Typography>
        </Button>
      )
    }
    return (
      <img
        src={imageData}
        alt={`axle-${axleNumber}-${type}-image`}
        style={{
          height: '56px',
          width: '75px',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
      />
    )
  }

  return (
    <>
      <ScalePopover
        scalePopoverData={scalePopoverData}
        setScalePopoverData={setScalePopoverData}
        callReadScale={callReadScale}
        onScaleSelected={scaleId => {
          callRequestImages(images, scaleId)
        }}
      />
      <Stack direction="row" spacing={1}>
        <Input
          name={`${pathPrepend}.${type}`}
          label={numberOfAxles > 1 ? t('axle_input_label', { axleNumber, type: t(type) }) : t(type)}
          variant="outlined"
          type="number"
          defaultValue={0}
          color={color}
          focused={canFlagManualScaleReadOverride && isManual ? true : undefined}
          disabled={disabled || isFetchingScaleReading || !hasEditPermission}
          control={control}
          inputRef={fieldRef}
          onClick={event => event.stopPropagation()}
          onChange={() => {
            if (canFlagManualScaleReadOverride) {
              setValue(`${pathPrepend}.isManual${startCase(toLower(type))}`, true)
            }
            callRequestImages(images)
          }}
          InputProps={{
            startAdornment,
            endAdornment: <InputAdornment position="end">{defaultYardUm}</InputAdornment>,
            onKeyPress: event => onKeyPressNumberField(event, { showWeightDecimals }),
          }}
          {...inputFieldProps}
          sx={{ flexGrow: 1 }}
        />
        {!disabled && showScaleButton ? (
          <Button
            name="read-scale"
            variant="outlined"
            size="small"
            onClick={event => {
              scaleButtonClickHandler(event)
            }}
            sx={{ width: '110px' }}
          >
            {t('take_weight')}
          </Button>
        ) : (
          ''
        )}
        <Badge
          badgeContent={images?.length}
          color="success"
          onClick={() => {
            const formData = getValues()
            const { axleWeights } = formatAxleWeightsFromFormData(formData, numberOfAxles)
            setMultiImageBladeData({
              id: materialData.id,
              name: materialData.name,
              materialName: materialData.materialName,
              axleNumber,
              axleWeights,
              gross: formData?.axles?.[`axle-${axleNumber}`]?.gross,
              tare: formData?.axles?.[`axle-${axleNumber}`]?.tare,
              grossImages: formData?.axles?.[`axle-${axleNumber}`]?.grossImages,
              tareImages: formData?.axles?.[`axle-${axleNumber}`]?.tareImages,
            })
          }}
        >
          {renderImagePreview()}
        </Badge>
      </Stack>
    </>
  )
}

AxleWeightsRow.propTypes = {
  type: PropTypes.oneOf(['gross', 'tare']).isRequired,
  axleNumber: PropTypes.number.isRequired,
  numberOfAxles: PropTypes.number,
  setAxleRefs: PropTypes.func.isRequired,
  setMultiImageBladeData: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  materialData: PropTypes.object,
}

AxleWeightsRow.defaultProps = {
  numberOfAxles: 1,
  disabled: false,
  materialData: null,
}

export default AxleWeightsRow
